import { useEffect, useState, useMemo } from "react";

import PropTypes from "prop-types";

import { AppProvider } from "@toolpad/core/AppProvider";
import { DashboardLayout } from "@toolpad/core/DashboardLayout";
import { DemoProvider, useDemoRouter } from "@toolpad/core/internal";

import Box from "@mui/material/Box";
import { Badge } from "@mui/material";
import { createTheme } from "@mui/material/styles";

import Notification from "../Notification/Notification";
import CustomerDashboard from "../CustomerDashboard/CustomerDashboard";
import ServicesDashboard from "../ServicesDashboard/ServicesDashboard";
import GalleryDashboard from "../GalleryDashboard/GalleryDashboard";
import NewsandUpdatesDashboard from "../NewsandUpdatesDashboard/NewsandUpdatesDashboard";
import HomePage from "../HomePage/HomePage";
import AboutUsDashboard from "../AboutUsDashboard/AboutUsDashboard";
import { getSession, logoutUser } from "../../auth";

import AnalyticsIcon from "@mui/icons-material/Analytics";
import MailIcon from "@mui/icons-material/Mail";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import OutboxIcon from "@mui/icons-material/Outbox";
import AllInboxIcon from "@mui/icons-material/AllInbox";
import CollectionsIcon from '@mui/icons-material/Collections';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import HandshakeIcon from '@mui/icons-material/Handshake';

const theme = createTheme({
  cssVariables: { colorSchemeSelector: "data-toolpad-color-scheme" },
  colorSchemes: { light: true, dark: true },
});

function DashboardContent({ pathname }) {
  if (pathname.startsWith("/messages")) {
    const type = pathname.split("/").pop();
    return <Notification type={type} />;
  }

  if (pathname == "/customers") {
    return <CustomerDashboard />;
  }

  if (pathname == "/services") {
    return <ServicesDashboard />;
  }
  if (pathname == "/gallery") {
    return <GalleryDashboard />;
  }
  if (pathname == "/newsandupdates") {
    return <NewsandUpdatesDashboard />;
  }
  if (pathname == "/aboutus") {
    return <AboutUsDashboard />;
  }
   if (pathname == "/home") {
    return <HomePage />;
  }

  return (
    <Box sx={{ p: 4 }}>
      <h2>Dashboard Overview</h2>
    </Box>
  );
}

DashboardContent.propTypes = {
  pathname: PropTypes.string.isRequired,
};

function useAuthSession() {
  const [session, setSessionState] = useState(() => {
    const user = getSession();
    return user ? { user } : null;
  });

  const signOut = () => {
    logoutUser();
    setSessionState(null);
  };

  return { session, signOut };
}

export default function Dashboard() {
  const router = useDemoRouter("/messages/all");
  const { session, signOut } = useAuthSession();

  useEffect(() => {
    if (!session) {
      router.navigate("/admin_login");
    }
  }, [session, router]);

  const authentication = useMemo(() => ({
    signIn: () => {}, // handled by login page
    signOut: () => {
      signOut();
      
    },
  }),[signOut, router]);

  if (!session) return null;

  return (
    <DemoProvider>
      <AppProvider
        session={session}
        authentication={authentication}
        router={router}
        theme={theme}
        branding={{ title: "VTAS ADMIN PANEL" }}
        navigation={[
          {
            segment: "overview",
            title: "Overview",
            icon: <AnalyticsIcon />,
          },
          {
            segment: "messages",
            title: "Messages",
            icon: <AllInboxIcon />,
            children: [
              { segment: "all", title: "All", icon: <MailIcon /> },
              {
                segment: "unread",
                title: "Unread",
                icon: (
                  <Badge badgeContent={5} color="error">
                    <MarkEmailUnreadIcon />
                  </Badge>
                ),
              },
              { segment: "replied", title: "Replied", icon: <OutboxIcon /> },
            ],
          },
          {
            segment: "customers",
            title: "Customers",
            icon: <PeopleAltIcon />,
          },
          {
            segment: "services",
            title: "Services",
            icon: <HandshakeIcon />,
          },
          //dev Ravindu
          {
            segment: "gallery",
            title: "Gallery",
            icon: <CollectionsIcon />,
          },
            {
            segment: "newsandupdates",
            title: "News and Updates",
            icon: <CollectionsIcon />,
          },
           {
            segment: "aboutus",
            title: "About Us",
            icon: <CollectionsIcon />,
          },
             {
            segment: "home",
            title: "Website",
            icon: <CollectionsIcon />,
          },
        ]}
      >
        <DashboardLayout>
          <DashboardContent pathname={router.pathname} />
        </DashboardLayout>
      </AppProvider>
    </DemoProvider>
  );
}
